import { NavLink, Outlet } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { CountryConfig } from "@orvis/core";
import { clearCountryChoice } from "../config.js";
import { Capacitor } from "@capacitor/core";
import { useTvMode } from "../hooks/useTvMode.js";
import { useBackButton } from "../hooks/useBackButton.js";

/**
 * Top-level chrome. Adapts to two navigation shapes:
 *   - Mobile / web: bottom-tab nav (thumb-friendly)
 *   - Android TV / 10-foot: left-side nav (D-pad friendly, keeps the
 *     current screen wide for landscape media)
 *
 * `useTvMode()` decides which and stamps `<html class="tv">` so the
 * Tailwind `tv:` variant kicks in across every component.
 *
 * On phones / web the DOCUMENT scrolls (sticky header, fixed bottom tabs),
 * not an inner overflow container: the native InlineBanner plugin follows
 * the WebView's own scroll to keep in-feed ads glued to their list rows (see
 * components/InlineAd.tsx). TV keeps a fixed-height shell with a scrolling
 * <main> so the side nav stays put.
 */

export function Layout({ country, canSwitch: canSwitchProp = false }: { country: CountryConfig; canSwitch?: boolean }) {
  const { t } = useTranslation();
  const tv = useTvMode();
  // The Android TV app is a single-country listing; never offer to switch
  // there, even if a local .env turned switching on for the web build.
  const canSwitch = canSwitchProp && !(tv && Capacitor.isNativePlatform());
  useBackButton();

  return (
    <div className="flex flex-col min-h-dvh bg-bg text-text tv:h-full tv:flex-row">
      {/* --- TV side nav (rendered only in TV mode; hidden on mobile/web) --- */}
      {tv ? (
        <nav
          className="hidden tv:flex flex-col gap-2 w-56 border-r border-border p-4"
          role="tablist"
          aria-label="Primary"
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="text-3xl leading-none" aria-hidden>
              {country.flag ?? "📺"}
            </span>
            <span className="font-semibold text-lg truncate">{country.name}</span>
          </div>
          <SideTab to="/discover" label={t("discover")} icon={iconDiscover} />
          <SideTab to="/news" label={t("news")} icon={iconNews} />
          <SideTab to="/tv" label={t("tv")} icon={iconTv} />
          <SideTab to="/radio" label={t("radio")} icon={iconRadio} />
          <div className="flex-1" />
          {canSwitch ? (
            <button
              type="button"
              onClick={clearCountryChoice}
              className="text-sm text-text/70 hover:text-text border border-border rounded px-3 py-2"
            >
              {t("change_country")}
            </button>
          ) : null}
        </nav>
      ) : null}

      {/* --- Main content --- */}
      <div className="flex-1 flex flex-col min-w-0 tv:min-h-0">
        {/* Mobile header (hidden in TV mode — the side nav has the country + switch).
            pt- adds the status-bar/notch inset on top of the normal 0.75rem padding
            so the header's content clears the system UI instead of sitting under it.
            Sticky because the document scrolls; data-ad-clip="top" tucks in-feed
            banners under it. */}
        <header
          data-ad-clip="top"
          className="sticky top-0 z-10 bg-bg border-b border-border px-4 pt-[calc(env(safe-area-inset-top)+0.75rem)] pb-3 flex items-center gap-3 tv:hidden"
        >
          <span className="text-xl leading-none" aria-hidden>
            {country.flag ?? "📺"}
          </span>
          <h1 className="text-lg font-semibold flex-1 truncate">{country.name}</h1>
          {canSwitch ? (
            <button
              type="button"
              onClick={clearCountryChoice}
              className="text-xs text-text/60 hover:text-text focus-visible:text-text border border-border rounded px-2 py-1"
              aria-label="Change country"
            >
              {t("change_country")}
            </button>
          ) : null}
        </header>

        <main className="flex-1 tv:overflow-y-auto pb-[calc(4rem+env(safe-area-inset-bottom)+var(--admob-banner-height,0px))] tv:pb-0">
          <Outlet />
        </main>

        {/* Mobile / web bottom-tab nav (hidden in TV mode).
            pb- adds the home-indicator/gesture-bar inset below the tab row so the
            system UI doesn't overlap the tabs (fixed-position elements ignore the
            WebView's own content insets, so this has to be explicit).
            bottom- shifts the whole bar above the pinned AdMob banner (iOS only now
            — Android phones use in-feed ads) using the height that ads.ts writes
            to --admob-banner-height once the banner loads. data-ad-clip="bottom"
            tucks in-feed banners under the bar. */}
        <nav
          data-ad-clip="bottom"
          className="fixed z-10 bottom-[var(--admob-banner-height,0px)] inset-x-0 border-t border-border bg-card/95 backdrop-blur pb-[env(safe-area-inset-bottom)] tv:hidden"
          role="tablist"
          aria-label="Primary"
        >
          <div className="flex max-w-screen-md mx-auto">
            <BottomTab to="/discover" label={t("discover")} icon={iconDiscover} />
            <BottomTab to="/news" label={t("news")} icon={iconNews} />
            <BottomTab to="/tv" label={t("tv")} icon={iconTv} />
            <BottomTab to="/radio" label={t("radio")} icon={iconRadio} />
          </div>
        </nav>
      </div>
    </div>
  );
}

const bottomBase =
  "flex-1 flex flex-col items-center justify-center py-2 text-[11px] " +
  "text-text/60 hover:text-text focus-visible:text-text";
const bottomActive = "text-text";

function BottomTab({ to, label, icon }: { to: string; label: string; icon: React.ReactNode }) {
  return (
    <NavLink to={to} className={({ isActive }) => `${bottomBase} ${isActive ? bottomActive : ""}`}>
      <span className="h-6 w-6 mb-1">{icon}</span>
      <span>{label}</span>
    </NavLink>
  );
}

const sideBase =
  "flex items-center gap-3 rounded-lg px-3 py-2 text-text/70 " +
  "hover:bg-card hover:text-text focus-visible:bg-card focus-visible:text-text";
const sideActive = "bg-card text-text";

function SideTab({ to, label, icon }: { to: string; label: string; icon: React.ReactNode }) {
  return (
    <NavLink to={to} className={({ isActive }) => `${sideBase} ${isActive ? sideActive : ""}`}>
      <span className="h-6 w-6">{icon}</span>
      <span>{label}</span>
    </NavLink>
  );
}

const iconDiscover = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="m14.5 9.5-2.5 5-5 2.5 2.5-5 5-2.5Z" />
  </svg>
);
const iconNews = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
    <path d="M4 5h13a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4V5Z" />
    <path d="M19 8h1a1 1 0 0 1 1 1v8a2 2 0 0 1-2 2" />
    <path d="M7 9h7M7 13h7M7 17h5" />
  </svg>
);
const iconTv = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
    <rect x="3" y="5" width="18" height="12" rx="2" />
    <path d="M8 21h8M12 17v4" />
  </svg>
);
const iconRadio = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
    <path d="M4 11h16v9H4z" />
    <circle cx="16" cy="15.5" r="2.5" />
    <path d="M4 11 20 4" />
  </svg>
);
